/**
 * Sorting utility functions for FamiliesPage
 */

/**
 * Case-insensitive alphabetical comparison (numeric-aware)
 * @param {*} a - First value
 * @param {*} b - Second value
 * @returns {number} Comparison result (-1, 0, 1)
 */
export function compareAlphabetical(a, b) {
    return String(a ?? '').localeCompare(String(b ?? ''), undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Sort objects alphabetically by a string property (default: name)
 * @param {Array} items - Array to sort
 * @param {string} nameKey - Property name to sort by
 * @returns {Array} New sorted array
 */
export function sortByName(items, nameKey = 'name') {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => compareAlphabetical(a?.[nameKey], b?.[nameKey]));
}

/**
 * Extract a stable sort key from a feature domain value (string or object)
 * @param {*} val - Feature value
 * @returns {string} Sort key
 */
export function getFeatureValueSortKey(val) {
    if (val === undefined || val === null) return '';
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        return String(val);
    }
    if (val.value !== undefined && val.value !== null) return String(val.value);
    if (val.Value !== undefined && val.Value !== null) return String(val.Value);
    if (val.displayText !== undefined && val.displayText !== null) return String(val.displayText);
    if (val.DisplayText !== undefined && val.DisplayText !== null) return String(val.DisplayText);
    return '';
}

/**
 * Sort feature domain values alphabetically by value
 * @param {Array} values - Feature values (strings or objects)
 * @returns {Array} New sorted array
 */
export function sortFeatureValues(values) {
    if (!Array.isArray(values)) return [];
    return [...values].sort((a, b) => {
        const orderA = Number(a?.sortOrder ?? a?.SortOrder);
        const orderB = Number(b?.sortOrder ?? b?.SortOrder);
        const hasA = Number.isFinite(orderA);
        const hasB = Number.isFinite(orderB);
        if (hasA && hasB && orderA !== orderB) return orderA - orderB;
        if (hasA && !hasB) return -1;
        if (!hasA && hasB) return 1;
        return compareAlphabetical(getFeatureValueSortKey(a), getFeatureValueSortKey(b));
    });
}

/**
 * Return object keys sorted alphabetically
 * @param {Object} obj - Object whose keys to sort
 * @returns {string[]} Sorted keys
 */
export function sortKeysAlphabetically(obj) {
    return Object.keys(obj || {}).sort(compareAlphabetical);
}

/**
 * Generic comparison function for sorting arrays of objects
 * @param {Object} a - First object to compare
 * @param {Object} b - Second object to compare  
 * @param {string} sortBy - Property name to sort by
 * @param {string} sortOrder - Sort order: 'asc' or 'desc'
 * @returns {number} Comparison result (-1, 0, 1)
 */
export function compareValues(a, b, sortBy, sortOrder) {
    let valA = a[sortBy] || '';
    let valB = b[sortBy] || '';
    
    // Handle date fields
    if (sortBy === 'createdAt' || sortBy === 'lastChangeDate' || sortBy === 'updatedAt' || sortBy === 'date') {
        valA = new Date(valA).getTime();
        valB = new Date(valB).getTime();
    } else if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = String(valB).toLowerCase();
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
}

/**
 * Comparison function specifically for activity feed items
 * @param {Object} a - First activity item
 * @param {Object} b - Second activity item
 * @param {string} activitySortBy - Property to sort by ('date', 'name', 'user')
 * @param {string} activitySortOrder - Sort order: 'asc' or 'desc'
 * @returns {number} Comparison result (-1, 0, 1)
 */
export function compareActivityItems(a, b, activitySortBy, activitySortOrder) {
    let valA, valB;
    
    if (activitySortBy === 'date' || activitySortBy === 'date_created') {
        valA = new Date(a.date).getTime();
        valB = new Date(b.date).getTime();
    } else if (activitySortBy === 'name') {
        valA = (a.item || '').toLowerCase();
        valB = (b.item || '').toLowerCase();
    } else if (activitySortBy === 'user') {
        valA = (a.user || '').toLowerCase();
        valB = (b.user || '').toLowerCase();
    } else {
        // Default to date sorting
        valA = new Date(a.date).getTime();
        valB = new Date(b.date).getTime();
    }

    if (valA < valB) return activitySortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return activitySortOrder === 'asc' ? 1 : -1;
    return 0;
}

/**
 * Sort an array of objects by a property
 * @param {Array} array - Array to sort
 * @param {string} sortBy - Property name to sort by
 * @param {string} sortOrder - Sort order: 'asc' or 'desc'
 * @returns {Array} New sorted array
 */
export function sortArray(array, sortBy, sortOrder) {
    return [...array].sort((a, b) => compareValues(a, b, sortBy, sortOrder));
}
